import { useState, useEffect, useCallback } from "react";

const API_BASE = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

export default function AppointmentsTab({
  businessId,
  apiBase = API_BASE,
  accentColor = "#6c63ff",
}) {
  const [bookings, setBookings] = useState([]);
  const [allUpcoming, setAllUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [updating, setUpdating] = useState(null);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

  // Fetch all upcoming bookings (global — no date filter)
  const fetchAllUpcoming = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/bookings?businessId=${businessId}`);
      const data = await res.json();
      // Only confirmed future bookings
      const upcoming = (data.bookings || []).filter(b =>
        b.status === "confirmed" && b.date >= today
      );
      setAllUpcoming(upcoming);
    } catch {}
  }, [businessId, apiBase, today]);

  // Fetch bookings for selected date
  const fetchBookings = useCallback(async () => {
    try {
      const res = await fetch(
        `${apiBase}/bookings?businessId=${businessId}&date=${selectedDate}`
      );
      const data = await res.json();
      setBookings(data.bookings || []);
    } catch {}
    setLoading(false);
  }, [businessId, selectedDate, apiBase]);

  useEffect(() => {
    setLoading(true);
    fetchBookings();
    fetchAllUpcoming();
    const iv = setInterval(() => { fetchBookings(); fetchAllUpcoming(); }, 15000);
    return () => clearInterval(iv);
  }, [fetchBookings, fetchAllUpcoming]);

  const updateStatus = async (bookingId, status) => {
    setUpdating(bookingId);
    try {
      await fetch(`${apiBase}/bookings/${bookingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await fetchBookings();
      await fetchAllUpcoming();
    } catch {}
    setUpdating(null);
  };

  const dateLabel = selectedDate === today ? "Today"
    : selectedDate === tomorrow ? "Tomorrow"
    : selectedDate === yesterday ? "Yesterday"
    : new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  const upcoming  = bookings.filter(b => b.status === "confirmed");
  const arrived   = bookings.filter(b => b.status === "arrived");
  const done      = bookings.filter(b => b.status === "done");
  const cancelled = bookings.filter(b => b.status === "cancelled" || b.status === "no-show");

  const statusStyle = {
    confirmed: { bg: "#f0f4ff", border: "#6c63ff", color: "#4338ca", label: "⏰ Confirmed" },
    arrived:   { bg: "#f0fdf4", border: "#22c55e", color: "#16a34a", label: "✅ Arrived" },
    done:      { bg: "#f9f9f9", border: "#e5e5e5", color: "#888",    label: "✓ Done" },
    cancelled: { bg: "#fff5f5", border: "#fca5a5", color: "#dc2626", label: "✕ Cancelled" },
    "no-show": { bg: "#fffbeb", border: "#fcd34d", color: "#d97706", label: "⚠ No-show" },
  };

  // Group upcoming by date for display
  const upcomingToShow = showAllUpcoming ? allUpcoming : allUpcoming.slice(0, 3);

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
      Loading appointments…
    </div>
  );

  return (
    <div style={{ padding: "16px 16px 40px", maxWidth: 520, margin: "0 auto" }}>

      {/* ── Global upcoming banner ── */}
      {allUpcoming.length > 0 && (
        <div style={{
          background: "#f0f4ff",
          border: `1.5px solid ${accentColor}`,
          borderRadius: 14, padding: "14px 16px", marginBottom: 20,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: accentColor }}>
              📅 Upcoming Bookings ({allUpcoming.length})
            </div>
            {allUpcoming.length > 3 && (
              <button
                style={{ background: "none", border: "none", fontSize: 12, color: accentColor, cursor: "pointer", fontWeight: 600 }}
                onClick={() => setShowAllUpcoming(v => !v)}
              >
                {showAllUpcoming ? "Show less" : `+${allUpcoming.length - 3} more`}
              </button>
            )}
          </div>
          {upcomingToShow.map(b => {
            const isToday = b.date === today;
            const isTomorrow = b.date === tomorrow;
            const dateStr = isToday ? "Today" : isTomorrow ? "Tomorrow"
              : new Date(b.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
            return (
              <div key={b.bookingId} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 0",
                borderTop: "1px solid #e0e8ff",
              }}>
                <div style={{ minWidth: 72 }}>
                  <div style={{ fontSize: 11, color: "#888", fontWeight: 600 }}>{dateStr}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "#111" }}>{b.timeLabel}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{b.customerName}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>✂ {b.serviceName}</div>
                </div>
                {b.servicePrice && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>₹{b.servicePrice}</div>
                )}
                <button
                  style={{
                    background: accentColor, color: "#fff", border: "none",
                    borderRadius: 8, padding: "5px 10px", fontSize: 11,
                    fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                  }}
                  onClick={() => setSelectedDate(b.date)}
                >
                  View
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Date selector ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button style={s.navBtn} onClick={() => {
          const d = new Date(selectedDate);
          d.setDate(d.getDate() - 1);
          setSelectedDate(d.toISOString().split("T")[0]);
        }}>‹</button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111" }}>{dateLabel}</div>
          <div style={{ fontSize: 12, color: "#aaa" }}>{selectedDate}</div>
        </div>
        <button style={s.navBtn} onClick={() => {
          const d = new Date(selectedDate);
          d.setDate(d.getDate() + 1);
          setSelectedDate(d.toISOString().split("T")[0]);
        }}>›</button>
      </div>

      {/* Quick date pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[
          { label: "Yesterday", val: yesterday },
          { label: "Today",     val: today },
          { label: "Tomorrow",  val: tomorrow },
        ].map(d => (
          <button key={d.val}
            style={{
              flex: 1, padding: "8px 4px", borderRadius: 10, border: "1px solid",
              borderColor: selectedDate === d.val ? accentColor : "#e5e5e5",
              background: selectedDate === d.val ? accentColor : "#fff",
              color: selectedDate === d.val ? "#fff" : "#666",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
            onClick={() => setSelectedDate(d.val)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Summary counts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
        {[
          { label: "Upcoming", count: upcoming.length, color: accentColor },
          { label: "Arrived",  count: arrived.length,  color: "#22c55e" },
          { label: "Done",     count: done.length,     color: "#888" },
        ].map(({ label, count, color }) => (
          <div key={label} style={{ background: "#fff", border: "1px solid #efefed", borderRadius: 12, padding: "12px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color }}>{count}</div>
            <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {bookings.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <div style={{ fontWeight: 600, color: "#666", marginBottom: 6 }}>No appointments {dateLabel.toLowerCase()}</div>
          <div style={{ fontSize: 13, color: "#aaa" }}>Customers can book from your catalogue page</div>
        </div>
      )}

      {/* Arrived */}
      {arrived.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>✅ Arrived — Ready to serve</div>
          {arrived.map(b => (
            <BookingCard key={b.bookingId} booking={b} style={statusStyle.arrived}
              onUpdate={updateStatus} updating={updating} accentColor={accentColor} />
          ))}
        </div>
      )}

      {/* Upcoming for selected date */}
      {upcoming.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>⏰ Upcoming</div>
          {upcoming.map(b => (
            <BookingCard key={b.bookingId} booking={b} style={statusStyle.confirmed}
              onUpdate={updateStatus} updating={updating} accentColor={accentColor} />
          ))}
        </div>
      )}

      {/* Done */}
      {done.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>✓ Done</div>
          {done.map(b => (
            <BookingCard key={b.bookingId} booking={b} style={statusStyle.done}
              onUpdate={updateStatus} updating={updating} accentColor={accentColor} />
          ))}
        </div>
      )}

      {/* Cancelled */}
      {cancelled.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>✕ Cancelled / No-show</div>
          {cancelled.map(b => (
            <BookingCard key={b.bookingId} booking={b}
              style={statusStyle[b.status] || statusStyle.cancelled}
              onUpdate={updateStatus} updating={updating} accentColor={accentColor} />
          ))}
        </div>
      )}

      <button onClick={() => { fetchBookings(); fetchAllUpcoming(); }} style={s.refreshBtn}>
        ↻ Refresh
      </button>
    </div>
  );
}

function BookingCard({ booking, style, onUpdate, updating, accentColor, showDone }) {
  const isUpdating = updating === booking.bookingId;

  return (
    <div style={{
      background: style.bg, border: `2px solid ${style.border}`,
      borderRadius: 14, padding: "14px 16px", marginBottom: 10,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#111", marginBottom: 2 }}>
            {booking.timeLabel}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: style.color }}>
            {style.label}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>{booking.customerName}</div>
          {booking.customerPhone && (
            <a href={`tel:${booking.customerPhone}`}
              style={{ fontSize: 12, color: "#6c63ff", textDecoration: "none" }}>
              {booking.customerPhone}
            </a>
          )}
        </div>
      </div>

      {/* Service */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: booking.note ? 8 : 0 }}>
        <span style={{ fontSize: 14, color: "#555", fontWeight: 500 }}>
          ✂ {booking.serviceName}
        </span>
        {booking.servicePrice && (
          <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
            ₹{booking.servicePrice.toLocaleString("en-IN")}
          </span>
        )}
      </div>

      {booking.note && (
        <div style={{ fontSize: 12, color: "#888", fontStyle: "italic", marginBottom: 8 }}>
          📝 {booking.note}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        {booking.status === "confirmed" && (
          <>
            <button
              style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: "#22c55e", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              onClick={() => onUpdate(booking.bookingId, "arrived")}
              disabled={isUpdating}
            >
              {isUpdating ? "…" : "✅ Mark Arrived"}
            </button>
            <button
              style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #fca5a5", background: "#fff0f0", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              onClick={() => { if (window.confirm("Mark as no-show?")) onUpdate(booking.bookingId, "no-show"); }}
              disabled={isUpdating}
            >
              No-show
            </button>
            <button
              style={{ padding: "9px 12px", borderRadius: 10, border: "1px solid #e5e5e5", background: "#fff", color: "#888", fontSize: 12, cursor: "pointer" }}
              onClick={() => { if (window.confirm("Cancel this appointment?")) onUpdate(booking.bookingId, "cancelled"); }}
              disabled={isUpdating}
            >
              Cancel
            </button>
          </>
        )}
        {booking.status === "arrived" && (
          <button
            style={{ flex: 1, padding: "9px", borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            onClick={() => onUpdate(booking.bookingId, "done")}
            disabled={isUpdating}
          >
            {isUpdating ? "…" : "✓ Mark Done"}
          </button>
        )}
      </div>
    </div>
  );
}

const s = {
  navBtn: {
    width: 36, height: 36, borderRadius: 10,
    border: "1px solid #e5e5e5", background: "#fff",
    fontSize: 18, cursor: "pointer", color: "#444",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  section: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: "#888",
    letterSpacing: 1, textTransform: "uppercase",
    marginBottom: 10,
  },
  refreshBtn: {
    width: "100%", background: "#f5f5f3",
    border: "1px solid #e5e5e5", borderRadius: 12,
    padding: 12, fontSize: 13, color: "#666",
    fontWeight: 600, cursor: "pointer", marginTop: 8,
  },
};
